import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonRefresher,
  IonRefresherContent,
  IonLoading,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonIcon,
  IonModal,
  IonButton,
  IonSelect,
  IonSelectOption,
  IonInput,
  IonTextarea,
  IonToast,
  IonText,
  IonFab,
  IonFabButton,
  IonChip,
  IonGrid,
  IonRow,
  IonCol,
} from "@ionic/react";
import { add, chevronDownCircleOutline, closeCircle, funnelOutline } from "ionicons/icons";
import { useRecoilValue } from "recoil";
import { userProfileState } from "../services/state.service";
import { useEffect, useState } from "react";
import { getEnquiries, updateEnquiry, createEnquiry, EnquiryStatus } from "../services/enquiry.service";
import { getSupportAgents } from "../services/ticket.service";
import EnquiryCard from "../components/EnquiryCard";
import EnquiryFilter from "../components/EnquiryFilter";
import SelectItem from "../components/SelectItem";

const PER_PAGE = 50;

const EnquiryList: React.FC = () => {
  const userProfile: any = useRecoilValue(userProfileState);
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [skip, setSkip] = useState(0);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState(false);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterData, setFilterData] = useState<any>();
  const [filterCriteria, setFilterCriteria] = useState<any>({});

  const [agents, setAgents] = useState<{ farmUserId: string; name: string }[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editComment, setEditComment] = useState('');
  const [editAssigneeId, setEditAssigneeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const emptyCreateForm = {
    name: '', mobile: '', email: '', location: '', message: '',
    expectedQuantity: '', expectedItemType: '', assigneeId: '',
  };
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState(false);
  const setCreateField = (key: string, value: string) => setCreateForm(f => ({ ...f, [key]: value }));
  const openCreateModal = () => {
    setCreateForm({ ...emptyCreateForm, assigneeId: userProfile?.farmUserId || '' });
    setShowCreateModal(true);
  };
  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateForm(emptyCreateForm);
  };

  const [showEditAssigneeModal, setShowEditAssigneeModal] = useState(false);
  const [showCreateAssigneeModal, setShowCreateAssigneeModal] = useState(false);

  useEffect(() => {
    getSupportAgents().then(setAgents).catch(() => setAgents([]));
  }, []);

  const buildWhere = () => {
    const and: any[] = [{ assigneeId: userProfile?.farmUserId }];
    if (filterCriteria?.and?.length) {
      // Any explicit filter (status, mobile, name, or location) searches across all statuses
      // unless status itself is one of the chosen filters.
      and.push(...filterCriteria.and);
    } else {
      // Untouched landing view: default to NEW/OPEN only.
      and.push({ status: { inq: [EnquiryStatus.NEW, EnquiryStatus.OPEN] } });
    }
    return { and };
  };

  const fetchData = async (event?: CustomEvent, isInitial: boolean = true) => {
    if (!userProfile?.farmUserId) return;

    if (isInitial) {
      setLoading(true);
      setHasError(false);
      setSkip(0);
      setDisableInfiniteScroll(false);
    }

    try {
      const where = buildWhere();
      const res = await getEnquiries(where, PER_PAGE, isInitial ? 0 : skip);
      if (isInitial) {
        setEnquiries(res);
      } else {
        setEnquiries(prev => [...prev, ...res]);
      }
      setDisableInfiniteScroll(res.length < PER_PAGE);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      setHasError(true);
    } finally {
      setLoading(false);
      (event as any)?.detail?.complete?.();
    }
  };

  useEffect(() => {
    fetchData(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCriteria, userProfile]);

  const handleRefresh = (e: any) => {
    fetchData(undefined, true);
    e.detail.complete();
  };

  const fetchNext = async (event: CustomEvent<void>) => {
    const nextSkip = skip + PER_PAGE;
    setSkip(nextSkip);
    await fetchData(undefined, false);
    (event.target as HTMLIonInfiniteScrollElement).complete();
  };

  const openEdit = (enquiry: any) => {
    setEditing(enquiry);
    setEditStatus(enquiry.status || '');
    setEditComment(enquiry.comment || '');
    setEditAssigneeId(enquiry.assigneeId || '');
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await updateEnquiry(editing.id, {
        status: editStatus,
        comment: editComment,
        assigneeId: editAssigneeId,
      });
      setEditing(null);
      await fetchData(undefined, true);
      setToastMessage('Enquiry updated');
    } catch (error) {
      console.error("Error updating enquiry:", error);
      setToastMessage('Failed to update enquiry');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.mobile.trim()) {
      setToastMessage('Name and mobile are required');
      return;
    }
    setCreating(true);
    try {
      await createEnquiry({
        name: createForm.name.trim(),
        mobile: createForm.mobile.trim(),
        email: createForm.email.trim() || undefined,
        location: createForm.location.trim() || undefined,
        message: createForm.message.trim() || undefined,
        assigneeId: createForm.assigneeId || userProfile?.farmUserId || undefined,
        expectedQuantity: createForm.expectedQuantity ? Number(createForm.expectedQuantity) : undefined,
        expectedItemType: createForm.expectedItemType.trim() || undefined,
      });
      setShowCreateModal(false);
      setCreateForm(emptyCreateForm);
      await fetchData(undefined, true);
      setToastMessage('Enquiry created');
    } catch (error) {
      console.error("Error creating enquiry:", error);
      setToastMessage('Failed to create enquiry');
    } finally {
      setCreating(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Enquiries</IonTitle>
          <IonButtons slot="end">
            <IonIcon
              icon={funnelOutline}
              style={{ fontSize: '22px', marginRight: '16px' }}
              onClick={() => setShowFilterModal(true)}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonLoading isOpen={loading} message="Loading enquiries..." spinner="circles" />

        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText="Pull to refresh"
            refreshingSpinner="circles"
            refreshingText="Refreshing..."
          />
        </IonRefresher>

        {hasError ? (
          <div style={{ color: "red", textAlign: "center", padding: "20px" }}>
            <p>Error fetching enquiries. Please refresh the page.</p>
          </div>
        ) : (
          <>
            {enquiries.length === 0 && !loading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: '8px' }}>
                <IonText color="medium" style={{ fontSize: '14px' }}>No enquiries found</IonText>
                <IonText color="medium" style={{ fontSize: '13px' }}>Pull to refresh</IonText>
              </div>
            )}
            {enquiries.map((enquiry, index) => (
              <EnquiryCard enquiry={enquiry} agents={agents} key={enquiry.id || index} onClick={openEdit} />
            ))}

            <IonInfiniteScroll
              disabled={disableInfiniteScroll}
              onIonInfinite={fetchNext}
              threshold="100px"
            >
              <IonInfiniteScrollContent
                loadingSpinner="bubbles"
                loadingText="Loading more enquiries..."
              />
            </IonInfiniteScroll>
          </>
        )}

        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={openCreateModal}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>
      </IonContent>

      <IonModal isOpen={showFilterModal} cssClass="my-custom-class">
        <EnquiryFilter
          filter={filterData}
          filterChangeHandler={setFilterData}
          onDoneHandler={setFilterCriteria}
          onCloseHandler={setShowFilterModal}
        />
      </IonModal>

      <IonModal isOpen={showCreateModal} onDidDismiss={closeCreateModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>New Enquiry</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={closeCreateModal}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonItem>
            <IonLabel position="stacked">Name *</IonLabel>
            <IonInput
              value={createForm.name}
              placeholder="Farmer name"
              onIonChange={(e) => setCreateField('name', e.detail.value || '')}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Mobile *</IonLabel>
            <IonInput
              value={createForm.mobile}
              placeholder="Mobile number"
              onIonChange={(e) => setCreateField('mobile', e.detail.value || '')}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Email</IonLabel>
            <IonInput
              value={createForm.email}
              placeholder="Email"
              onIonChange={(e) => setCreateField('email', e.detail.value || '')}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Location</IonLabel>
            <IonInput
              value={createForm.location}
              placeholder="Location"
              onIonChange={(e) => setCreateField('location', e.detail.value || '')}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Expected Quantity</IonLabel>
            <IonInput
              type="number"
              value={createForm.expectedQuantity}
              placeholder="Expected quantity"
              onIonChange={(e) => setCreateField('expectedQuantity', e.detail.value || '')}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Item Type</IonLabel>
            <IonInput
              value={createForm.expectedItemType}
              placeholder="e.g. pack(s) of 10 traps"
              onIonChange={(e) => setCreateField('expectedItemType', e.detail.value || '')}
            />
          </IonItem>

          <IonItem lines="none">
            <IonLabel>Assign To</IonLabel>
          </IonItem>
          <IonItem>
            <IonGrid>
              <IonRow onClick={() => setShowCreateAssigneeModal(true)}>
                {createForm.assigneeId ? (
                  <IonCol>
                    <IonChip outline={true} color="primary">
                      <IonLabel>{agents.find(a => a.farmUserId === createForm.assigneeId)?.name || createForm.assigneeId}</IonLabel>
                      <IonIcon
                        icon={closeCircle}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreateField('assigneeId', '');
                        }}
                      />
                    </IonChip>
                  </IonCol>
                ) : (
                  <IonCol>
                    <IonLabel style={{ color: 'var(--ion-color-medium)' }}>Unassigned</IonLabel>
                  </IonCol>
                )}
              </IonRow>
            </IonGrid>
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Message</IonLabel>
            <IonTextarea
              value={createForm.message}
              placeholder="Enquiry message"
              onIonChange={(e) => setCreateField('message', e.detail.value || '')}
              autoGrow
            />
          </IonItem>

          <IonButton expand="block" style={{ marginTop: 16 }} disabled={creating} onClick={handleCreate}>
            {creating ? 'Creating...' : 'Create'}
          </IonButton>
        </IonContent>
      </IonModal>

      <IonModal isOpen={!!editing} onDidDismiss={() => setEditing(null)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Edit Enquiry</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setEditing(null)}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {editing && (
            <>
              <IonItem lines="none">
                <IonLabel>
                  <h2 style={{ fontWeight: 'bold' }}>{editing.name}</h2>
                  <p>{editing.mobile}{editing.email ? ` · ${editing.email}` : ''}</p>
                  {editing.location && <p>{editing.location}</p>}
                  {editing.message && <p style={{ marginTop: 8 }}>{editing.message}</p>}
                </IonLabel>
              </IonItem>

              <IonItem>
                <IonLabel>Status</IonLabel>
                <IonSelect value={editStatus} onIonChange={(e) => setEditStatus(e.detail.value)}>
                  <IonSelectOption value={EnquiryStatus.NEW}>New</IonSelectOption>
                  <IonSelectOption value={EnquiryStatus.OPEN}>Open</IonSelectOption>
                  <IonSelectOption value={EnquiryStatus.PURCHASED}>Purchased</IonSelectOption>
                  <IonSelectOption value={EnquiryStatus.NOT_PURCHASED}>Not Purchased</IonSelectOption>
                </IonSelect>
              </IonItem>

              <IonItem lines="none">
                <IonLabel>Assign To</IonLabel>
              </IonItem>
              <IonItem>
                <IonGrid>
                  <IonRow onClick={() => setShowEditAssigneeModal(true)}>
                    {editAssigneeId ? (
                      <IonCol>
                        <IonChip outline={true} color="primary">
                          <IonLabel>{agents.find(a => a.farmUserId === editAssigneeId)?.name || editAssigneeId}</IonLabel>
                          <IonIcon
                            icon={closeCircle}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditAssigneeId('');
                            }}
                          />
                        </IonChip>
                      </IonCol>
                    ) : (
                      <IonCol>
                        <IonLabel style={{ color: 'var(--ion-color-medium)' }}>Unassigned</IonLabel>
                      </IonCol>
                    )}
                  </IonRow>
                </IonGrid>
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Comment</IonLabel>
                <IonTextarea
                  value={editComment}
                  placeholder="Add a comment"
                  onIonChange={(e) => setEditComment(e.detail.value || '')}
                  autoGrow
                />
              </IonItem>

              <IonButton expand="block" style={{ marginTop: 16 }} disabled={saving} onClick={handleSave}>
                {saving ? 'Saving...' : 'Save'}
              </IonButton>
            </>
          )}
        </IonContent>
      </IonModal>

      {showCreateAssigneeModal && (
        <IonModal isOpen={showCreateAssigneeModal}>
          <SelectItem
            label="Assign To"
            items={agents}
            labelKey="name"
            valueKey="farmUserId"
            setItem={(item: any) => {
              setCreateField('assigneeId', item.farmUserId);
              setShowCreateAssigneeModal(false);
            }}
            selectedValue={createForm.assigneeId}
            onClose={setShowCreateAssigneeModal}
          />
        </IonModal>
      )}

      {showEditAssigneeModal && (
        <IonModal isOpen={showEditAssigneeModal}>
          <SelectItem
            label="Assign To"
            items={agents}
            labelKey="name"
            valueKey="farmUserId"
            setItem={(item: any) => {
              setEditAssigneeId(item.farmUserId);
              setShowEditAssigneeModal(false);
            }}
            selectedValue={editAssigneeId}
            onClose={setShowEditAssigneeModal}
          />
        </IonModal>
      )}

      <IonToast
        isOpen={!!toastMessage}
        message={toastMessage}
        duration={2000}
        onDidDismiss={() => setToastMessage('')}
      />
    </IonPage>
  );
};

export default EnquiryList;
